import { format } from "date-fns";
import {
  contractorColor,
  contractorKey,
  requestTypeStatusColor,
  getStatusColor,
} from "@utils/ptoHelpers";
import { DayEventsPopover } from "./DayEventsPopover";

export function CalendarDayCell({
  day,
  dateStr,
  inMonth,
  dayList,
  onRequestClick,
  onShowMore,
  moreForDay,
  onCloseMore,
  showAll = false,
  minHeightClass = "tw-min-h-[84px]",
  plantFilter = "0",
  statusFilter = "",
  highlightedRequestId,
}: {
  day: Date;
  dateStr: string;
  inMonth: boolean;
  dayList: any[];
  onRequestClick: (request: any) => void;
  onShowMore: () => void;
  moreForDay: string | null;
  onCloseMore: () => void;
  showAll?: boolean;
  minHeightClass?: string;
  plantFilter?: string;
  statusFilter?: string;
  highlightedRequestId?: number | null;
}) {
  const visibleList = showAll ? dayList : dayList.slice(0, 3);
  const todayStr = format(new Date(), "yyyy-MM-dd");

  return (
    <div
      data-date={dateStr}
      className={`${minHeightClass} tw-border tw-rounded-md tw-relative ${
        inMonth
          ? "tw-bg-white dark:tw-bg-[#0D1117]"
          : "tw-bg-gray-50 dark:tw-bg-[#0A1E2D]"
      }${dateStr === todayStr ? "tw-ring-1 tw-ring-blue-400" : ""}`}
    >
      <div className="tw-text-[11px] tw-text-gray-600 dark:tw-text-gray-400 tw-p-1">
        {format(day, "d")}
      </div>

      <div className="tw-mt-1 tw-space-y-1 tw-relative">
        {visibleList.map((r) => {
          const startStr = String(r.start_date).slice(0, 10);
          const endStr = String(r.end_date).slice(0, 10);
          const startsHere = startStr === dateStr;
          const endsHere = endStr === dateStr;

          const contractor =
            r.contractor_name || r.user_email || r.user_id || "";

          // Use status+type colors when both filters are "All"
          const useDefaultColors = plantFilter === "0" && statusFilter === "";
          const { bg, text } = useDefaultColors
            ? requestTypeStatusColor(r.status, r.request_type)
            : contractorColor(contractorKey(r));

          const mainText = r.description ? r.description : "";
          const statusText = (r.status || "").toString();
          const requestTypeText = r.request_type ? ` (${r.request_type})` : "";
          const hoursText =
            typeof r.hours_requested === "number"
              ? ` • ${r.hours_requested}h`
              : "";

          const statusColorClass = getStatusColor(r.status, r.request_type);

          const isHighlighted =
            highlightedRequestId != null && r.id === highlightedRequestId;

          return (
            <div
              key={`${r.id}-${dateStr}`}
              className={`${bg} ${text} tw-text-[10px] tw-leading-tight tw-px-1 tw-py-0.5 ${
                isHighlighted
                  ? "tw-ring-2 tw-ring-offset-1 tw-ring-blue-500 "
                  : ""
              }${
                startsHere && endsHere
                  ? "tw-rounded tw-m-2"
                  : startsHere
                  ? "tw-rounded-l tw-ml-2"
                  : endsHere
                  ? "tw-rounded-r tw-mr-2"
                  : ""
              } tw-overflow-hidden tw-whitespace-nowrap tw-cursor-pointer`}
              title={`${contractor} • ${mainText}${requestTypeText} • ${statusText}${hoursText}`}
              onClick={() => onRequestClick(r)}
            >
              {contractor} • {mainText}
              {requestTypeText} •{" "}
              <span
                className={`tw-font-semibold tw-bg-white tw-px-1 tw-rounded ${statusColorClass}`}
              >
                {statusText}
              </span>
              {endsHere ? hoursText : ""}
            </div>
          );
        })}
        {!showAll && dayList.length > 3 && (
          <div
            className="tw-text-[10px] tw-text-gray-600 tw-cursor-pointer tw-underline"
            onClick={(e) => {
              e.stopPropagation();
              onShowMore();
            }}
          >
            +{dayList.length - 3} more
          </div>
        )}

        {!showAll && moreForDay === dateStr && (
          <DayEventsPopover
            dateStr={dateStr}
            dayList={dayList}
            onRequestClick={onRequestClick}
            onClose={onCloseMore}
            plantFilter={plantFilter}
            statusFilter={statusFilter}
            highlightedRequestId={highlightedRequestId}
          />
        )}
      </div>
    </div>
  );
}
